import { ArrowLeft } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '@clareefai/ui';
import PageTitle from '../../components/shared/PageTitle';
import AssignSalespersonModal from '../../components/prospects/AssignSalespersonModal';
import EditProspectModal from '../../components/prospects/EditProspectModal';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import MeetingTabs from '../../components/prospects/detail/MeetingTabs';
import ProspectHero from '../../components/prospects/detail/ProspectHero';
import type { ProspectView, PromoterView, SalespersonView } from '../../components/prospects/detail/types';
import { useAbility } from '../../contexts/AbilityContext';
import { useGetVendorMeetingsQuery } from '../../store/meetingApi';
import { useGetPromotersQuery, type PromoterResponse } from '../../store/promoterApi';
import {
  useGetProspectByIdQuery,
  useDeleteProspectMutation,
  useResendProspectInvitationMutation,
  useUpdateProspectMutation,
  type ProspectResponse,
} from '../../store/prospectApi';
import { type TeamMemberResponse, useGetTeamMembersQuery } from '../../store/teamMemberApi';

const toProspectView = (p: ProspectResponse): ProspectView => ({
  id: p.id,
  name: `${p.firstName} ${p.lastName}`,
  email: p.email,
  phone: p.phone || '',
  company: p.company || '',
  status: p.status,
  sectors: p.sectors ?? [],
  companySize: p.companySize ?? undefined,
  createdDate: p.createdAt || new Date().toISOString(),
  // Legacy prospects may predate assignedPromoters and only carry promoterId.
  assignedPromoters: p.assignedPromoters?.length
    ? p.assignedPromoters
    : (p.promoterId ? [p.promoterId] : []),
  assignedSalespersonId: p.assignedSalespersonId ?? undefined,
});

const toPromoterView = (p: PromoterResponse): PromoterView => ({
  id: p.id,
  name: `${p.firstName} ${p.lastName}`,
  company: p.company || '',
  avatar: p.avatar,
  prospectsReferred: p.prospectsReferred ?? 0,
  conversionRate: p.conversionRate ?? 0,
});

const toSalespersonView = (m: TeamMemberResponse): SalespersonView => ({
  name: m.name,
  role: m.role,
  avatar: m.avatar,
});

const ProspectDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ability = useAbility();
  const canEditProspect = ability.can('update', 'Prospect');
  const canDeleteProspect = ability.can('delete', 'Prospect');

  const { data: prospectData, isLoading: prospectLoading, error: prospectError, refetch } = useGetProspectByIdQuery(id || '');
  const { data: promotersData } = useGetPromotersQuery();
  const { data: meetingsData } = useGetVendorMeetingsQuery();
  const { data: teamMembers = [] } = useGetTeamMembersQuery();
  const [deleteProspect, { isLoading: isDeleting }] = useDeleteProspectMutation();
  const [resendInvitation, { isLoading: isResending }] = useResendProspectInvitationMutation();
  const [updateProspect] = useUpdateProspectMutation();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignSalesperson, setShowAssignSalesperson] = useState(false);

  const prospect = prospectData ? toProspectView(prospectData) : null;

  if (prospectLoading || !prospect) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {prospectLoading && !prospectError ? t('common.loading') : t('prospects.notFound')}
          </h2>
          <button onClick={() => navigate('/prospects')} className="btn-primary">
            {t('prospects.backToProspects')}
          </button>
        </div>
      </div>
    );
  }

  const prospectMeetings = (meetingsData ?? []).filter(m => m.prospect_id === id);
  const assignedPromoters = (promotersData ?? [])
    .filter(p => prospect.assignedPromoters.includes(p.id))
    .map(toPromoterView);
  const salespersonMember = teamMembers.find(m => m.id === prospect.assignedSalespersonId);
  const salesperson = salespersonMember ? toSalespersonView(salespersonMember) : null;

  const handleResendInvite = async () => {
    if (!id) return;
    try {
      await resendInvitation(id).unwrap();
      toast.success(t('prospects.magicLinkSent'));
    } catch {
      toast.error(t('prospects.magicLinkFailed'));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProspect(prospect.id).unwrap();
      toast.success(t('prospects.deletedSuccess'));
      setShowDeleteModal(false);
      navigate('/prospects');
    } catch (err: any) {
      toast.error(err?.data?.message || t('prospects.deleteFailed'));
    }
  };

  // TODO: date validation from this page is not wired to the API yet (pre-existing stub).
  const handleValidateDate = (promoterId: string, dateId: string) => {
    console.log('Validating date', dateId, 'for promoter', promoterId);
  };

  const handleAssignSalesperson = async (salespersonId: string) => {
    try {
      await updateProspect({ id: prospect.id, data: { assigned_salesperson_id: salespersonId } }).unwrap();
      toast.success(t('prospects.assign.success'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('prospects.assign.failed'));
    }
  };

  return (
    <>
      <PageTitle title={`${prospect.name} · Prospect`} />
      <div className="space-y-6">
        <button
          onClick={() => navigate('/prospects')}
          className="group -ml-1 flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
          <span>{t('prospects.backToProspects')}</span>
        </button>

        <ProspectHero
          prospect={prospect}
          hasLoggedIn={prospectData?.hasLoggedIn}
          invitedBy={prospectData?.invitedBy}
          salesperson={salesperson}
          canEdit={canEditProspect}
          canDelete={canDeleteProspect}
          isResendingInvite={isResending}
          onEdit={() => setShowEditModal(true)}
          onResendInvite={handleResendInvite}
          onDelete={() => setShowDeleteModal(true)}
          onAssignSalesperson={() => setShowAssignSalesperson(true)}
        />

        <MeetingTabs
          promoters={assignedPromoters}
          meetings={prospectMeetings}
          onOpenPromoter={(promoterId) => navigate(`/promoters/${promoterId}`)}
          onValidateDate={handleValidateDate}
        />
      </div>

      <ConfirmDialog
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t('prospects.deleteTitle')}
        description={t('prospects.deleteConfirm', { name: prospect.name })}
        confirmLabel={t('prospects.deletePermanently')}
        variant="danger"
        isLoading={isDeleting}
      />

      {showEditModal && prospectData && (
        <EditProspectModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            refetch();
          }}
          prospect={{
            id: prospectData.id,
            email: prospectData.email,
            firstName: prospectData.firstName,
            lastName: prospectData.lastName,
            jobTitle: prospectData.jobTitle,
            phone: prospectData.phone,
            company: prospectData.company,
            companySize: prospectData.companySize,
            sectors: prospectData.sectors,
            notes: prospectData.notes,
            source: prospectData.source,
            status: prospectData.status,
          }}
        />
      )}

      {showAssignSalesperson && (
        <AssignSalespersonModal
          currentSalespersonId={prospect.assignedSalespersonId}
          onAssign={handleAssignSalesperson}
          onClose={() => setShowAssignSalesperson(false)}
        />
      )}
    </>
  );
};

export default ProspectDetail;
